import React, { createContext, useContext, useEffect, useState } from 'react';
import { preferencesApi } from 'src/services/api';
import { useAuth } from './AuthContext';

export interface UserPreferences {
  id: string;
  user_id: string;
  language: string;
  theme: 'light' | 'dark' | 'system';
  email_notifications: boolean;
  push_notifications: boolean;
  ai_language: string;
  date_format: string;
  time_format: '12h' | '24h';
  timezone: string;
  created_at: string;
  updated_at: string;
}

export interface Language {
  code: string;
  name: string;
  nativeName: string;
  direction: 'ltr' | 'rtl';
}

export interface Theme {
  id: string;
  name: string;
  description: string;
}

export interface Timezone {
  id: string;
  name: string;
  offset: string;
}

interface UserPreferencesContextType {
  preferences: UserPreferences | null;
  loading: boolean;
  error: string | null;
  languages: Language[];
  themes: Theme[];
  timezones: Timezone[];
  updatePreferences: (updates: Partial<UserPreferences>) => Promise<boolean>;
  refreshPreferences: () => Promise<void>;
  getAILanguagePrompt: () => string;
}

const defaultPreferences: UserPreferences = {
  id: '',
  user_id: '',
  language: 'en',
  theme: 'light',
  email_notifications: true,
  push_notifications: true,
  ai_language: 'en',
  date_format: 'MM/DD/YYYY',
  time_format: '12h',
  timezone: 'UTC',
  created_at: '',
  updated_at: '',
};

const UserPreferencesContext = createContext<UserPreferencesContextType | undefined>(undefined);

export const UserPreferencesProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { user } = useAuth();
  const [preferences, setPreferences] = useState<UserPreferences | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [languages, setLanguages] = useState<Language[]>([]);
  const [themes, setThemes] = useState<Theme[]>([]);
  const [timezones, setTimezones] = useState<Timezone[]>([]);

  const fetchPreferences = async () => {
    if (!user) {
      setPreferences(null);
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError(null);

      const [prefsData, langsData, themesData, tzsData] = await Promise.all([
        preferencesApi.getPreferences(),
        preferencesApi.getLanguages(),
        preferencesApi.getThemes(),
        preferencesApi.getTimezones(),
      ]);

      setPreferences(prefsData || defaultPreferences);
      setLanguages(langsData || []);
      setThemes(themesData || []);
      setTimezones(tzsData || []);
    } catch (err: any) {
      console.error('Failed to fetch preferences:', err);
      setError(err.message || 'Failed to load preferences');
      setPreferences(defaultPreferences);
    } finally {
      setLoading(false);
    }
  };

  const refreshPreferences = async () => {
    await fetchPreferences();
  };

  const updatePreferences = async (updates: Partial<UserPreferences>): Promise<boolean> => {
    try {
      setError(null);
      const updatedPrefs = await preferencesApi.updatePreferences(updates);
      if (updatedPrefs) {
        setPreferences(updatedPrefs);
        return true;
      }
      return false;
    } catch (err: any) {
      console.error('Failed to update preferences:', err);
      setError(err.message || 'Failed to update preferences');
      return false;
    }
  };

  // Generate AI language instruction based on user preference
  const getAILanguagePrompt = (): string => {
    const aiLang = preferences?.ai_language || 'en';

    const languageInstructions: Record<string, string> = {
      en: 'Please respond in English.',
      ar: 'الرجاء الرد باللغة العربية. Please respond in Arabic.',
      fr: 'Veuillez répondre en français. Please respond in French.',
      es: 'Por favor responda en español. Please respond in Spanish.',
      de: 'Bitte antworten Sie auf Deutsch. Please respond in German.',
      zh: '请用中文回复。Please respond in Chinese.',
      ja: '日本語でお答えください。Please respond in Japanese.',
      ko: '한국어로 답변해 주세요. Please respond in Korean.',
      pt: 'Por favor, responda em português. Please respond in Portuguese.',
      ru: 'Пожалуйста, ответьте на русском языке. Please respond in Russian.',
      tr: 'Lütfen Türkçe yanıt verin. Please respond in Turkish.',
      hi: 'कृपया हिंदी में जवाब दें। Please respond in Hindi.',
    };

    return languageInstructions[aiLang] || languageInstructions.en;
  };

  useEffect(() => {
    fetchPreferences();
  }, [user]);

  // Apply theme based on preference
  useEffect(() => {
    if (!preferences) return;

    const applyTheme = (theme: 'light' | 'dark') => {
      document.documentElement.setAttribute('data-theme', theme);
      // If using MUI, you might want to trigger theme change here
    };

    if (preferences.theme === 'system') {
      const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
      applyTheme(mediaQuery.matches ? 'dark' : 'light');

      const handler = (e: MediaQueryListEvent) => {
        applyTheme(e.matches ? 'dark' : 'light');
      };

      mediaQuery.addEventListener('change', handler);
      return () => mediaQuery.removeEventListener('change', handler);
    } else {
      applyTheme(preferences.theme);
    }
  }, [preferences?.theme]);

  // Apply RTL direction for Arabic and other RTL languages
  useEffect(() => {
    if (!preferences) return;

    const lang = languages.find(l => l.code === preferences.language);
    if (lang) {
      document.documentElement.setAttribute('dir', lang.direction);
      document.documentElement.setAttribute('lang', lang.code);
    }
  }, [preferences?.language, languages]);

  const value = {
    preferences,
    loading,
    error,
    languages,
    themes,
    timezones,
    updatePreferences,
    refreshPreferences,
    getAILanguagePrompt,
  };

  return (
    <UserPreferencesContext.Provider value={value}>
      {children}
    </UserPreferencesContext.Provider>
  );
};

export const useUserPreferences = () => {
  const context = useContext(UserPreferencesContext);
  if (context === undefined) {
    throw new Error('useUserPreferences must be used within a UserPreferencesProvider');
  }
  return context;
};
